import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';

/**
 * Route guard that blocks access to public-only routes (e.g. `/auth`) when
 * the user is already authenticated.
 *
 * @remarks
 * Awaits {@link AuthService.initialized} before reading the signal, mirroring
 * the same boot-ordering guarantee as {@link authGuard}. Without this wait,
 * a page reload would always let the login page render because
 * `restoreStoredToken()` is async and the signal starts as `null`.
 *
 * Redirects to `/dashboard` when the user already holds a valid session,
 * so closing and reopening the browser on `/auth` sends them straight back
 * to the app instead of showing a blank login form.
 *
 * @example
 * ```ts
 * // In app.routes.ts
 * { path: 'auth', loadChildren: ..., canActivate: [guestGuard] }
 * ```
 */
export const guestGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  await auth.initialized;
  return auth.isAuthenticated() ? router.parseUrl('/dashboard') : true;
};
